<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { listEditions } from '@/api/events'
import type { Event } from '@/types/events'
import SidebarNavItem from '@/components/layout/sidebar/SidebarNavItem.vue'
import SidebarSectionDivider from '@/components/layout/sidebar/SidebarSectionDivider.vue'
import SidebarEventSwitcher from '@/components/layout/sidebar/SidebarEventSwitcher.vue'
import SidebarEditionSwitcher from '@/components/layout/sidebar/SidebarEditionSwitcher.vue'

const props = defineProps<{ eventSlug: string; editionSlug: string }>()
const emit = defineEmits<{ (e: 'navigate'): void }>()

const { t } = useI18n()
const router = useRouter()
const route = useRoute()

async function handleEventSelect(event: Event): Promise<void> {
  const editions = await listEditions(event.slug)
  const target = editions.find((e) => e.active) ?? editions[0] ?? null

  const editionBase = `/organizer/events/${props.eventSlug}/editions/${props.editionSlug}`
  const subPath = route.path.startsWith(editionBase) ? route.path.slice(editionBase.length) : ''

  if (target) {
    await router.push(`/organizer/events/${event.slug}/editions/${target.slug}${subPath}`)
  } else {
    await router.push(`/organizer/events/${event.slug}`)
  }
}

const STORAGE_KEY = 'sidebar_edition_roadmap_open'
const roadmapOpen = ref(localStorage.getItem(STORAGE_KEY) !== 'false')

watch(roadmapOpen, (val) => localStorage.setItem(STORAGE_KEY, String(val)))

const FLAG_ICON = [
  'M3 3v1.5M3 21v-6m0 0l2.77-.693a9 9 0 016.208.682l.108.054a9 9 0 006.086.71l3.114-.732a48.524 48.524 0 01-.005-10.499l-3.11.732a9 9 0 01-6.085-.711l-.108-.054a9 9 0 00-6.208-.682L3 4.5M3 15V4.5',
]

const USERS_ICON = [
  'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z',
]

const HAND_RAISED_ICON = [
  'M10.05 4.575a1.575 1.575 0 10-3.15 0v3m3.15-3v-1.5a1.575 1.575 0 013.15 0v1.5m-3.15 0l.075 5.925m3.075.75V4.575m0 0a1.575 1.575 0 013.15 0V15M6.9 7.575a1.575 1.575 0 10-3.15 0v8.175a6.75 6.75 0 006.75 6.75h2.018a5.25 5.25 0 003.712-1.538l1.732-1.732a5.25 5.25 0 001.538-3.712l-.001-1.286a3.75 3.75 0 00-.709-2.251l-1.13-1.509a1.125 1.125 0 00-.897-.448h-.115',
]

const COG_ICON = [
  'M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z',
  'M15 12a3 3 0 11-6 0 3 3 0 016 0z',
]

const CALENDAR_ICON = [
  'M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5',
]

const STAR_ICON = [
  'M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z',
]

const WRENCH_ICON = [
  'M21.75 6.75a4.5 4.5 0 01-4.884 4.484c-1.076-.091-2.264.071-2.95.904l-7.152 8.684a2.548 2.548 0 11-3.586-3.586l8.684-7.152c.833-.686.995-1.874.904-2.95a4.5 4.5 0 016.336-4.486l-3.276 3.276a3.004 3.004 0 002.25 2.25l3.276-3.276c.256.565.398 1.192.398 1.852z',
]

const MAP_PIN_ICON = [
  'M15 10.5a3 3 0 11-6 0 3 3 0 016 0z',
  'M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z',
]

const INFO_ICON = [
  'M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z',
]

const LIST_ICON = [
  'M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z',
]

const CHART_ICON = [
  'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z',
]
</script>

<template>
  <div class="flex flex-col">
    <SidebarEventSwitcher
      :current-event-slug="props.eventSlug"
      @navigate="emit('navigate')"
      @select="handleEventSelect"
    />
    <SidebarEditionSwitcher
      :current-event-slug="props.eventSlug"
      :current-edition-slug="props.editionSlug"
      @navigate="emit('navigate')"
    />

    <!-- Back link -->
    <div class="px-4 py-3 border-b border-slate-800">
      <RouterLink
        :to="`/organizer/events/${props.eventSlug}`"
        class="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors"
        @click="emit('navigate')"
      >
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        {{ t('nav.sidebar.back_to_event') }}
      </RouterLink>
    </div>

    <nav class="flex flex-col py-3" aria-label="Edition navigation">
      <SidebarNavItem
        :to="`/organizer/events/${props.eventSlug}/editions/${props.editionSlug}`"
        :label="t('nav.sidebar.races')"
        :exact-active="true"
        :icon="FLAG_ICON"
        @navigate="emit('navigate')"
      />
      <SidebarNavItem
        :to="`/organizer/events/${props.eventSlug}/editions/${props.editionSlug}/participants`"
        :label="t('nav.sidebar.participants')"
        :icon="USERS_ICON"
        @navigate="emit('navigate')"
      />
      <SidebarNavItem
        :to="`/organizer/events/${props.eventSlug}/editions/${props.editionSlug}/volunteers`"
        :label="t('nav.sidebar.volunteers')"
        :icon="HAND_RAISED_ICON"
        @navigate="emit('navigate')"
      />
      <SidebarNavItem
        :to="`/organizer/events/${props.eventSlug}/editions/${props.editionSlug}/results`"
        :label="t('nav.sidebar.edition_results')"
        :icon="CHART_ICON"
        @navigate="emit('navigate')"
      />
      <SidebarNavItem
        :to="`/organizer/events/${props.eventSlug}/editions/${props.editionSlug}/settings`"
        :label="t('nav.sidebar.settings')"
        :icon="COG_ICON"
        @navigate="emit('navigate')"
      />

      <SidebarSectionDivider />

      <p class="pl-4 pr-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">
        {{ t('nav.sidebar.public_website') }}
      </p>

      <SidebarNavItem
        :to="`/organizer/events/${props.eventSlug}/editions/${props.editionSlug}/programme`"
        :label="t('nav.sidebar.programme')"
        :icon="CALENDAR_ICON"
        @navigate="emit('navigate')"
      />
      <SidebarNavItem
        :to="`/organizer/events/${props.eventSlug}/editions/${props.editionSlug}/sponsors`"
        :label="t('nav.sidebar.sponsors')"
        :icon="STAR_ICON"
        @navigate="emit('navigate')"
      />
      <SidebarNavItem
        :to="`/organizer/events/${props.eventSlug}/editions/${props.editionSlug}/gear-items`"
        :label="t('nav.sidebar.gear_list')"
        :icon="WRENCH_ICON"
        @navigate="emit('navigate')"
      />
      <SidebarNavItem
        :to="`/organizer/events/${props.eventSlug}/editions/${props.editionSlug}/aid-stations`"
        :label="t('nav.sidebar.aid_stations')"
        :icon="MAP_PIN_ICON"
        @navigate="emit('navigate')"
      />
      <SidebarNavItem
        :to="`/organizer/events/${props.eventSlug}/editions/${props.editionSlug}/practical-info`"
        :label="t('nav.sidebar.practical_info')"
        :icon="INFO_ICON"
        @navigate="emit('navigate')"
      />

      <SidebarSectionDivider />

      <!-- Collapsible coming-soon section -->
      <button
        type="button"
        class="flex items-center gap-2 pl-4 pr-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600 hover:text-slate-500 transition-colors w-full text-left"
        :aria-expanded="roadmapOpen"
        @click="roadmapOpen = !roadmapOpen"
      >
        <span class="flex-1">{{ t('nav.sidebar.coming_soon') }}</span>
        <svg
          class="w-3.5 h-3.5 transition-transform shrink-0"
          :class="roadmapOpen ? 'rotate-0' : '-rotate-90'"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <template v-if="roadmapOpen">
        <SidebarNavItem :label="t('nav.sidebar.start_lists')" :icon="LIST_ICON" :disabled="true" />
      </template>
    </nav>
  </div>
</template>
